<script setup lang="ts">
const props = withDefaults(defineProps<{
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
}>(), {
  size: 'md',
  disabled: false,
})

const modelValue = defineModel<number | null>({ default: null })

const hoverValue = ref<number | null>(null)

const svgSize = computed(() => {
  switch (props.size) {
    case 'sm':
      return 20
    case 'lg':
      return 32
    default:
      return 24
  }
})

const gapClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'gap-0.5'
    case 'lg':
      return 'gap-2'
    default:
      return 'gap-1'
  }
})

function handleClick(star: number) {
  if (props.disabled) return
  modelValue.value = star
}

function handleMouseEnter(star: number) {
  if (props.disabled) return
  hoverValue.value = star
}

function handleMouseLeave() {
  hoverValue.value = null
}

function handleKeyDown(event: KeyboardEvent, star: number) {
  if (props.disabled) return
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    modelValue.value = star
  }
}

function isStarFilled(star: number): boolean {
  const displayValue = hoverValue.value ?? modelValue.value
  return displayValue !== null && star <= displayValue
}
</script>

<template>
  <div
    class="inline-flex"
    :class="[gapClass, { 'opacity-50 pointer-events-none': disabled }]"
    role="radiogroup"
    aria-label="Hodnocení"
    @mouseleave="handleMouseLeave"
  >
    <button
      v-for="star in 5"
      :key="star"
      type="button"
      class="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded transition-transform"
      :class="[!disabled && 'hover:scale-110 cursor-pointer']"
      :aria-label="`${star} z 5 hvězdiček`"
      :aria-checked="modelValue === star"
      role="radio"
      :tabindex="disabled ? -1 : 0"
      @click="handleClick(star)"
      @mouseenter="handleMouseEnter(star)"
      @keydown="handleKeyDown($event, star)"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        :width="svgSize"
        :height="svgSize"
        viewBox="0 0 24 24"
        :fill="isStarFilled(star) ? 'currentColor' : 'none'"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        :class="isStarFilled(star) ? 'text-yellow-400' : 'text-dimmed'"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </button>
  </div>
</template>
